<script lang="ts" setup>
import {
  ToccoBaseModuleFieldsFragment,
  ToccoCourseFieldsFragment,
  ToccoELearningFieldsFragment,
  ToccoIntroModuleFieldsFragment,
  ToccoModuleFieldsFragment,
  ToccoWebinarFieldsFragment,
} from '~/api/graphql/generated'
import type { ParsedToccoModuleFieldsFragment } from '~/types'

export type entityProp =
  | ToccoWebinarFieldsFragment
  | ToccoCourseFieldsFragment
  | ToccoModuleFieldsFragment
  | ParsedToccoModuleFieldsFragment
  | ToccoIntroModuleFieldsFragment
  | ToccoELearningFieldsFragment
  | ToccoBaseModuleFieldsFragment

const props = defineProps<{
  entity: entityProp
}>()

const decodedToccoData = computed(() => {
  const clonedEntity = JSON.parse(JSON.stringify(props.entity))
  if (typeof clonedEntity.fieldToccoData === 'string') {
    try {
      clonedEntity.fieldToccoData = JSON.parse(clonedEntity.fieldToccoData)
    } catch {
      clonedEntity.fieldToccoData = null
    }
  }
  if (!clonedEntity.fieldToccoData?.paths) {
    clonedEntity.fieldToccoData = { paths: {} }
  }
  return clonedEntity
})

const { t } = useI18n()

const fields = [
  {
    key: 'targets',
    type: 'list',
  },
  {
    key: 'competencies',
    type: 'list',
  },
  {
    key: 'contents',
    type: 'list',
  },
  {
    key: 'target_group',
    type: 'text',
  },
  {
    key: 'teaching_method',
    type: 'text',
  },
  {
    key: 'requirements',
    type: 'list',
  },
  {
    key: 'duration',
    type: 'text',
  },
  {
    key: 'certificate',
    type: 'text',
  },
  {
    key: 'costs',
    type: 'text',
  },
  {
    key: 'notes',
    type: 'notes',
  },
]

const baseModuleFieldKeys = [
  'targets',
  'contents',
  'target_group',
  'teaching_method',
  'duration',
  'certificate',
  'costs',
  'notes',
]

const activeFields = computed(() =>
  props.entity.__typename === 'ToccoBaseModule'
    ? fields.filter((field) => baseModuleFieldKeys.includes(field.key))
    : fields,
)

export interface ItemsProps {
  id: string
  title: string
  items: {
    type: string
    content: string | string[]
  }
}

const items = computed(() => {
  const items: ItemsProps[] = []

  activeFields.value.forEach((field) => {
    if (!decodedToccoData.value.fieldToccoData.paths[field.key]?.value) return

    const content = decodedToccoData.value.fieldToccoData.paths[field.key].value

    const item: ItemsProps = {
      id: field.key,
      title: t(`tocco.detail.accordionTitles.${field.key}`),
      items: {
        type: '',
        content: '',
      },
    }

    if ('text' === field.type) {
      item.items = {
        type: field.type,
        content: content.split('\n').join('<br/>'),
      }
    }

    if ('list' === field.type) {
      item.items = {
        type: field.type,
        content: content.split('\n').map((item: string) => item),
      }
    }

    if ('notes' === field.type) {
      item.items = {
        type: field.type,
        content: content.split('\n').join('<br/>'),
      }
    }

    items.push(item)
  })

  return items
})
</script>

<template>
  <Accordion multiple>
    <AccordionItem
      v-for="(item, index) in items"
      :key="index"
      v-animate="{ animation: 'vertical-reveal' }"
      :index="index"
      :title="item.title"
      title-tag="h2"
    >
      <div v-if="'text' === item.items.type" v-html="item.items.content" />
      <div v-if="'list' === item.items.type">
        <ul>
          <li v-for="(listItem, listItemIndex) in item.items.content" :key="listItemIndex" v-html="listItem" />
        </ul>
      </div>

      <div v-if="'notes' === item.items.type" v-html="item.items.content" />
    </AccordionItem>
  </Accordion>
</template>
